import { useState } from 'react';
import {
  BarChartOutlined,
  HomeOutlined,
  MenuOutlined,
  PlusOutlined,
  ToolOutlined,
} from '@ant-design/icons';
import { Drawer, Menu, Typography } from 'antd';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import { stripPathLanguage } from '../../i18nLanguages';
import usePermission from '../../hooks/usePermission';
import MyButtonSecondary from '../myButton/MyButtonSecondary';
import { getSelectedKey, useAdminMenuItems } from './AdminSider';
import { useAdminUserActions } from './AdminUserMenu';

const NEW_TICKET_PATH = '/job_repairs/edit';

// Groups are shown open (no expanding) so every destination is visible.
const flattenGroups = (items) =>
  items.map((item) =>
    item.children
      ? { type: 'group', key: item.key, label: item.label, children: item.children }
      : item
  );

// Phone navigation: the main sider entries as tabs, everything else in the
// "Menu" sheet. Tabs follow the same permission filter as the sider.
const AdminBottomNav = ({ onSelect }) => {
  const { t } = useTranslation();
  const { pathname } = useLocation();
  const { can } = usePermission();
  const menuItems = useAdminMenuItems();
  const { userName, menuItems: userItems, handleMenuClick } =
    useAdminUserActions();
  const [menuOpen, setMenuOpen] = useState(false);
  const selectedKey = getSelectedKey(stripPathLanguage(pathname));

  const allowed = (key) => menuItems.some((item) => item.key === key);

  const tabs = [
    allowed('/dashboard') && {
      key: '/dashboard',
      icon: <HomeOutlined />,
      label: t('WORD_HOME'),
    },
    allowed('/job_repairs') && {
      key: '/job_repairs',
      icon: <ToolOutlined />,
      label: t('WORD_TICKETS'),
    },
    can('addJobRepairs') && can('useJobRepairReception') && {
      key: NEW_TICKET_PATH,
      icon: <PlusOutlined />,
      label: t('JR_NEW_TICKET'),
      main: true,
    },
    allowed('/reports') && {
      key: '/reports',
      icon: <BarChartOutlined />,
      label: t('WORD_REPORTS'),
    },
  ].filter(Boolean);

  const tabKeys = tabs.map((tab) => tab.key);
  const sheetItems = [
    ...flattenGroups(menuItems.filter((item) => !tabKeys.includes(item.key))),
    {
      type: 'group',
      key: 'account',
      label: t('WORD_ACCOUNT'),
      children: userItems.map((item) =>
        item.key === 'logout' ? { ...item, danger: true } : item
      ),
    },
  ];
  const menuSelected = !tabKeys.includes(selectedKey);

  const handleSheetClick = ({ key }) => {
    setMenuOpen(false);
    if (key.startsWith('/')) onSelect?.(key);
    else handleMenuClick({ key });
  };

  return (
    <>
      <nav className="admin_bottom_nav">
        {tabs.map((tab) => (
          <button
            key={tab.key}
            type="button"
            className={`admin_bottom_nav_item${tab.main ? ' is_main' : ''}${
              selectedKey === tab.key ? ' is_active' : ''
            }`}
            aria-current={selectedKey === tab.key ? 'page' : undefined}
            onClick={() => onSelect?.(tab.key)}
          >
            <span className="admin_bottom_nav_icon">{tab.icon}</span>
            <span className="admin_bottom_nav_label">{tab.label}</span>
          </button>
        ))}
        <button
          type="button"
          className={`admin_bottom_nav_item${menuSelected ? ' is_active' : ''}`}
          aria-expanded={menuOpen}
          onClick={() => setMenuOpen(true)}
        >
          <span className="admin_bottom_nav_icon">
            <MenuOutlined />
          </span>
          <span className="admin_bottom_nav_label">{t('WORD_MENU')}</span>
        </button>
      </nav>

      <Drawer
        open={menuOpen}
        placement="bottom"
        size="auto"
        title={
          <span className="admin_menu_sheet_title">
            <span>{t('WORD_MENU')}</span>
            <Typography.Text type="secondary">{userName}</Typography.Text>
          </span>
        }
        onClose={() => setMenuOpen(false)}
        classNames={{ wrapper: 'admin_more_sheet' }}
        styles={{ body: { padding: 8 } }}
        footer={
          <MyButtonSecondary
            block
            label={t('WORD_CLOSE')}
            onClick={() => setMenuOpen(false)}
            className="admin_menu_sheet_close"
          />
        }
      >
        <Menu
          mode="inline"
          items={sheetItems}
          selectedKeys={[selectedKey]}
          onClick={handleSheetClick}
          className="admin_more_menu"
        />
      </Drawer>
    </>
  );
};

export default AdminBottomNav;
